'use client';

import { cn } from '@/lib/utils';
import Image from 'next/image';
import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { PageChallengeOldDesignSchema } from '@/sanity/pages/PageChallengeOldDesignTemplate';
import ChipSerif from '@/components/global/ui/ChipSerif';
import checkbox from '@/public/challenge/checkbox.svg';
import { motion } from 'framer-motion';
import { CTAButton } from '@/shadcn/ui/button';

export default function ForWhoSection({
  data,
  className,
}: {
  data: PageChallengeOldDesignSchema;
  className?: string;
}) {
  return (
    <section className={cn('relative overflow-clip', className)}>
      <div className="container flex flex-col items-center pt-[8rem] lg:pt-[15.25rem] pb-[7rem] lg:pb-[20rem] relative">
        {data?.forWhoTitle && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.4 }}
            className="richtext sm:w-[45rem] lg:w-[min(105rem,100%)] prose-p:w-[min(75rem,100%)] flex flex-col gap-[2rem]"
          >
            <PortableText
              value={data.forWhoTitle}
              components={richTextComponent}
            />
          </motion.div>
        )}

        <div className="w-[min(180.125rem,100%)] grid md:grid-cols-2 lg:grid-cols-3 gap-[2.25rem] lg:gap-[3.5rem] mt-[3rem] lg:mt-[4rem]">
          {data?.forWhoList &&
            data?.forWhoList.length &&
            data?.forWhoList.map((card, index) => (
              <motion.div
                key={index}
                className="relative"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{
                  duration: 0.25,
                  delay: index * 0.08,
                  ease: [0.22, 0.61, 0.36, 1],
                }}
              >
                <div className="h-full flex flex-col gap-[2.75rem] justify-between rounded-[2rem] bg-white/90 px-[4.75rem] py-[3.5rem] border border-[#615B50]/10">
                  <div
                    className={cn(
                      'w-[7rem] font-geist h-[7rem] min-w-[7rem] min-h-[7rem] rounded-[1.375rem] flex items-center justify-center mb-[3rem] text-white text-[3rem] bg-[#103037]',
                    )}
                  >
                    {index + 1}
                  </div>

                  {card?.text && (
                    <div className="richtext richtext-start prose-headings:text-[#103037] prose-headings:font-geist prose-headings:text-[3rem] prose-headings:font-medium prose-headings:tracking-[-0.09rem] [text-wrap-style:pretty] flex flex-col gap-[1.5rem]">
                      <PortableText
                        value={card.text}
                        components={richTextComponent}
                      />
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
        </div>

        <motion.div
          className="mt-[4rem] lg:mt-[6rem] w-full lg:w-fit mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <CTAButton data={data.forWhoCTA} />
        </motion.div>
      </div>
    </section>
  );
}
